/**
 * @todo simplify implementation; separate navbar and sidebar
 */

import React from 'react';

import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import {
  AppBar,
  Container,
  Grid,
  Toolbar,
  Card,
  Typography,
  units,
} from '@hbf/dsl';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import StudapartIcon from 'ha/custom-icons/studapart.svg';

import { ListingLinkToPartner } from '../../components/ListingLinkToPartner';
import { ListingAdvertiserCard } from '../../containers/ListingAdvertiserCard';
import { ListingInstantBookingBlock } from '../ListingInstantBookingBlock';
import { AnchorLink } from '../../components/AnchorLink';
import { ListingPrice } from '../ListingPrice';

import { useStudapart } from '../../hooks/useStudapart';
import { StudapartInfo } from '../../components/StudapartInfo';

const useStyles = makeStyles<
  void,
  'stickyNavSideContainerPartner' | 'hidden'
>()((theme, _params, classes) => ({
  stickyNav: {
    position: 'absolute',
    top: -theme.spacing(6),
    zIndex: 9,
    width: '100%',
    height: units.rem(3),
  },
  stickyNavRedesign: {
    position: 'absolute',
    top: -units.rem(6),
    zIndex: 9,
    width: '100%',
    height: units.rem(3.425),
  },
  stickyNavActive: {
    bottom: 'initial',
    background: theme.palette.grey['100'],
    [`& .${classes.stickyNavSideContainerPartner}`]: {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      transform: 'translate3d(0, 5rem, 0)',
    },
  },
  stickyNavSideContainer: {
    position: 'absolute',
    borderRadius: units.rem(0.4),
    transition: '0.3s ease-out',
    left: 0,
  },
  stickyNavSideContainerSize: {
    width: '300px',
  },
  navSideContainerSize: {
    width: '384px',
  },
  stickyNavSideContainerPartner: {
    top: -units.rem(2),
  },
  userContainer: {
    top: '100%',
    right: 0,
    left: 'unset',
  },
  topFixed: {
    top: '100%',
  },
  withPadding: {
    padding: theme.spacing(2),
  },
  hidden: {
    visibility: 'hidden',
    [`& *:not(.${classes.hidden})`]: {
      visibility: 'visible',
    },
  },
  toolBar: {
    background: theme.palette.common.white,
  },
  studapartIcon: {
    width: 'auto',
    height: units.rem(1.25),
    marginTop: theme.spacing(1),
  },
}));
interface Props {
  isSticky: boolean;
  wasSticky: boolean;
  isBelongsToPartner: boolean;
  style: React.CSSProperties;
  onStickyHeightChange: (height: number) => void;
}

export const getStyle = (style: React.CSSProperties, isSticky: boolean) => {
  if (!isSticky) {
    return {};
  }

  const leftStyle = style.left
    ? typeof style.left === 'string'
      ? parseInt(style.left, 10)
      : style.left
    : 0;

  const widthStyle = style.width
    ? typeof style.width === 'string'
      ? parseInt(style.width, 10)
      : style.width
    : 0;

  const left = leftStyle + widthStyle;

  if (left === 0) {
    return {};
  }

  return {
    // 300px is the width of the advertiser card
    // we should align it to right side
    left: `${left - 300}px`,
  };
};

const ListingOverviewDesktop: React.FC<Props> = React.forwardRef<
  HTMLDivElement,
  Props
>(
  (
    { isSticky, wasSticky, onStickyHeightChange, isBelongsToPartner, style },
    ref,
  ) => {
    const { T } = useIntl();
    const { classes, cx } = useStyles();
    const { lg } = useMediaQuery();

    const sidebarRef = React.useRef<HTMLDivElement>(null);

    const heightSet = React.useRef(false);

    React.useEffect(() => {
      if (heightSet.current) {
        return;
      }

      if (!wasSticky && isSticky && sidebarRef.current) {
        heightSet.current = true;
        onStickyHeightChange(sidebarRef.current.offsetHeight);
      }
    }, [isSticky, wasSticky, onStickyHeightChange]);

    const { width, ...restStyle } = style;

    const { isStudapartListing } = useStudapart();

    return (
      <div
        ref={ref}
        style={{
          ...restStyle,
          left: 0,
          paddingLeft: style.left,
        }}
        className={cx(classes.stickyNav, {
          [classes.stickyNavActive]: isSticky,
          [classes.hidden]: !isSticky,
        })}
        data-test-locator="ListingOverviewDesktop"
      >
        <Grid
          container
          className={cx({
            [classes.hidden]: !isSticky,
          })}
        >
          {isSticky && (
            <AppBar color="default">
              <Toolbar className={classes.toolBar}>
                <Container>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <Grid item xs>
                      <ListingPrice variant="desktop-appbar" />
                    </Grid>
                    <Grid item xs="auto">
                      <Grid container spacing={4} justifyContent="flex-end">
                        <Track name="Clicked LDP navigation" linkType="Basics">
                          <Grid item>
                            <AnchorLink to="basics">{T('Basics')}</AnchorLink>
                          </Grid>
                        </Track>
                        <Track
                          name="Clicked LDP navigation"
                          linkType="Facilities"
                        >
                          <Grid item>
                            <AnchorLink to="facilities" offset={-36}>
                              {T('Facilities')}
                            </AnchorLink>
                          </Grid>
                        </Track>
                        <Track name="Clicked LDP navigation" linkType="Pricing">
                          <Grid item>
                            <AnchorLink to="rental-conditions">
                              {T('Pricing')}
                            </AnchorLink>
                          </Grid>
                        </Track>

                        <Track name="Clicked LDP navigation" linkType="FAQ">
                          <Grid item>
                            <AnchorLink to="faq">{T('ldp.faq.nav')}</AnchorLink>
                          </Grid>
                        </Track>

                        {!isBelongsToPartner ? (
                          <Track
                            name="Clicked LDP navigation"
                            linkType="Advertiser"
                          >
                            <Grid item>
                              <AnchorLink to="advertiser" offset={0}>
                                {T('Advertiser')}
                              </AnchorLink>
                            </Grid>
                          </Track>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                </Container>
              </Toolbar>
            </AppBar>
          )}

          {
            // if style.top is undefined => sticky navbar is not visible yet, user didn't scroll enough => show advertiser / partner card
            // if style.top is positive number => user scrolled enough to make sticky navbar visible => still show advertiser / partner card
            // if style.top is negative number => user scrolled until map is visible, sticky navbar is above the visible screen => hide advertiser / partner card
            lg && !(style.top && style.top < 0) && (
              <Grid item>
                {isBelongsToPartner ? (
                  <Card
                    className={cx(
                      classes.stickyNavSideContainer,
                      classes.stickyNavSideContainerPartner,
                      classes.withPadding,
                      classes.userContainer,
                      { [classes.topFixed]: isSticky },
                    )}
                    style={getStyle(style, isSticky)}
                    variant="outlined"
                    ref={sidebarRef}
                    data-test-locator="ListingOverviewDesktop"
                  >
                    {isStudapartListing && (
                      <React.Fragment>
                        <Typography.Caption>
                          {T('ldp.studapart.caption')}
                        </Typography.Caption>
                        <StudapartIcon className={classes.studapartIcon} />
                      </React.Fragment>
                    )}
                    <ListingLinkToPartner
                      label={
                        isStudapartListing
                          ? T('View on %s', 'Studapart')
                          : undefined
                      }
                    />
                    {isStudapartListing ? (
                      <StudapartInfo />
                    ) : (
                      <Typography.Paragraph align="center">
                        <b>{T('Note:')}</b>{' '}
                        {T(
                          'please be aware that this partner website may not offer the same safety guarantees as Housing Anywhere.',
                        )}
                      </Typography.Paragraph>
                    )}
                  </Card>
                ) : (
                  <div
                    className={cx(
                      classes.stickyNavSideContainer,
                      classes.userContainer,
                      { [classes.topFixed]: isSticky },
                      classes.stickyNavSideContainerSize,
                    )}
                    style={getStyle(style, isSticky)}
                    ref={sidebarRef}
                    data-test-locator="ListingOverviewDesktop"
                  >
                    {/* @ts-ignore */}
                    <ListingAdvertiserCard
                      hideVerifications
                      hideUserAvatarSection={isSticky}
                    >
                      <ListingInstantBookingBlock />
                    </ListingAdvertiserCard>
                  </div>
                )}
              </Grid>
            )
          }
        </Grid>
      </div>
    );
  },
);

export { ListingOverviewDesktop };
